import { PdfPreview } from "./PdfPreview";
import React from "react";
import { ArrowLeft, Edit, Plus, Printer, Save, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CostSheetMaterialLineDialog } from "@/components/manufacturing/CostSheetMaterialLineDialog";
import { CostSheetProcessMaterialsView } from "@/components/manufacturing/CostSheetProcessMaterialsView";
import { useToast } from "@/components/ui/use-toast";
import { DUPLICATE_FAMILY_RULES, formatFamilyTemplateOption } from "@/lib/manufacturingDuplicate";
import {
  deleteTemplate,
  deleteTemplateLine,
  fetchFamilies,
  fetchMaterials,
  fetchSuppliers,
  reorderTemplateLines,
  fetchProcesses,
  fetchTemplates,
  formatMoney,
  saveTemplate,
  saveTemplateLine,
  setTemplateMaterialDestinationOnly,
  setTemplateMaterialExcluded,
} from "@/lib/api/manufacturing";

const initialTemplate = {
  name: "",
  description: "",
  template_kind: "mixed",
  is_active: true,
  cod_fam: "",
};

const initialProcessLine = {
  line_type: "process",
  process_id: "",
  sort_order: 0,
  default_quantity: 1,
  default_unit_cost: "",
  notes: "",
};

const templateKindLabels = {
  mixed: "Proceso con materiales",
  processes: "Solo procesos",
  materials: "Legacy plano",
};

function sortByOrder(left, right) {
  return (Number(left.sort_order || 0) - Number(right.sort_order || 0)) || String(left.id || "").localeCompare(String(right.id || ""));
}

function processLabel(line, processes) {
  const process = processes.find((item) => item.id === line.process_id);
  return process?.description || "Proceso";
}

function materialLabel(line, materials) {
  const material = line.material || materials.find((item) => item.id === line.material_id);
  return material ? `${material.code} · ${material.internal_description}` : "Material";
}

function processOwnCost(line) {
  return Number(Number(line.default_quantity || 0) * Number(line.default_unit_cost || 0));
}

function materialCost(line) {
  return Number(
    (
      Number(line.default_quantity || 0) *
      (1 + Number(line.default_waste_pct || 0) / 100) *
      Number(line.default_unit_cost || 0)
    ).toFixed(4)
  );
}

function templateTotals(template) {
  const processLines = template?.process_lines || [];
  const processTotalValue = processLines.reduce((sum, line) => sum + processOwnCost(line), 0);
  const materialTotalValue = processLines.reduce(
    (sum, line) => sum + (line.child_lines || []).reduce((childSum, child) => childSum + materialCost(child), 0),
    0
  );
  return {
    processTotal: processTotalValue,
    materialTotal: materialTotalValue,
    total: processTotalValue + materialTotalValue,
  };
}

function toViewMaterialLine(line, materials) {
  const material = line.material || materials.find((item) => item.id === line.material_id) || null;
  return {
    ...line,
    material,
    quantity: line.quantity ?? line.default_quantity,
    unit: line.unit || material?.default_unit || "",
    unit_cost: line.unit_cost ?? line.default_unit_cost,
    waste_pct: line.waste_pct ?? line.default_waste_pct ?? 0,
    material_supplier: line.material_supplier,
  };
}

function toViewProcessLines(processLines, processes, materials) {
  return processLines.map((line) => ({
    ...line,
    process: { description: processLabel(line, processes) },
    quantity: line.default_quantity,
    unit_cost: line.default_unit_cost,
    material_lines: (line.child_lines || []).map((child) => toViewMaterialLine(child, materials)),
  }));
}

export function TemplateManager() {
  const [printUrl, setPrintUrl] = React.useState(null);
  const [printing, setPrinting] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);
  const [families, setFamilies] = React.useState([]);
  const [materials, setMaterials] = React.useState([]);
  const [suppliers, setSuppliers] = React.useState([]);
  const [processes, setProcesses] = React.useState([]);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const [formData, setFormData] = React.useState(initialTemplate);
  const [activeProcessId, setActiveProcessId] = React.useState("");
  const [processLineEditingId, setProcessLineEditingId] = React.useState(null);
  const [processLine, setProcessLine] = React.useState(initialProcessLine);
  const [materialDialogOpen, setMaterialDialogOpen] = React.useState(false);
  const [materialEditingLine, setMaterialEditingLine] = React.useState(null);
  const { toast } = useToast();

  const loadAll = React.useCallback(
    async (nextSelectedTemplateId = null) => {
      const [templateResult, familyResult, materialResult, supplierResult, processResult] = await Promise.all([
        fetchTemplates(),
        fetchFamilies(),
        fetchMaterials(),
        fetchSuppliers("", { activeOnly: true }),
        fetchProcesses({ activeOnly: true }),
      ]);
      if (templateResult.error || familyResult.error || materialResult.error || supplierResult.error || processResult.error) {
        toast({ title: "Error", description: "No se pudieron cargar plantillas", variant: "destructive" });
      }

      const nextTemplates = templateResult.data || [];
      setTemplates(nextTemplates);
      setFamilies(familyResult.data || []);
      setMaterials(materialResult.data || []);
      setSuppliers(supplierResult.data || []);
      setProcesses(processResult.data || []);
      setSelectedTemplate((current) => {
        const id = nextSelectedTemplateId || current?.id;
        return id ? nextTemplates.find((item) => item.id === id) || null : null;
      });
    },
    [toast]
  );

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const processLines = React.useMemo(
    () => [...(selectedTemplate?.process_lines || [])].sort(sortByOrder),
    [selectedTemplate]
  );
  const viewProcessLines = React.useMemo(
    () => toViewProcessLines(processLines, processes, materials),
    [materials, processLines, processes]
  );
  const activeProcess = processLines.find((line) => line.id === activeProcessId) || processLines[0] || null;
  const orphanLines = selectedTemplate?.orphan_material_lines || [];
  const totals = templateTotals(selectedTemplate);
  const sheetTotals = {
    processOwnTotal: totals.processTotal,
    materialTotal: totals.materialTotal,
    total: totals.total,
  };

  React.useEffect(() => {
    if (!editorOpen || !selectedTemplate) return;
    if (!processLines.length) {
      setActiveProcessId("");
      return;
    }
    if (!activeProcessId || !processLines.some((line) => line.id === activeProcessId)) {
      setActiveProcessId(processLines[0].id);
    }
  }, [activeProcessId, editorOpen, processLines, selectedTemplate]);

  const openNewTemplate = () => {
    setEditorOpen(true);
    setEditingId(null);
    setSelectedTemplate(null);
    setFormData(initialTemplate);
    setActiveProcessId("");
    resetProcessLine();
    closeMaterialDialog();
  };

  const openTemplate = (template) => {
    setEditorOpen(true);
    setEditingId(template.id);
    setSelectedTemplate(template);
    setFormData({
      name: template.name || "",
      description: template.description || "",
      template_kind: template.template_kind || "mixed",
      is_active: template.is_active !== false,
      cod_fam: template.cod_fam?.trim() || template.family?.cod_fam?.trim() || "",
    });
    setActiveProcessId(template.process_lines?.[0]?.id || "");
    resetProcessLine();
    closeMaterialDialog();
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
    setSelectedTemplate(null);
    setFormData(initialTemplate);
    setActiveProcessId("");
    resetProcessLine();
    closeMaterialDialog();
  };

  function resetProcessLine() {
    setProcessLineEditingId(null);
    setProcessLine(initialProcessLine);
  }

  function closeMaterialDialog() {
    setMaterialDialogOpen(false);
    setMaterialEditingLine(null);
  }

  const submitTemplate = async (event) => {
    event.preventDefault();
    if (!formData.cod_fam.trim()) {
      toast({ title: "Validación", description: "Selecciona la familia de la plantilla", variant: "destructive" });
      return;
    }
    if (!formData.name.trim()) {
      toast({ title: "Validación", description: "El nombre de la plantilla es obligatorio", variant: "destructive" });
      return;
    }

    const result = await saveTemplate(formData, editingId);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar la plantilla", variant: "destructive" });
      return;
    }

    setEditingId(result.data?.id || editingId);
    toast({ title: editingId ? "Plantilla actualizada" : "Plantilla creada" });
    await loadAll(result.data?.id || editingId);
  };

  const removeTemplate = async (template) => {
    if (!window.confirm(`¿Eliminar la plantilla "${template.name}"?`)) return;
    const result = await deleteTemplate(template.id);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo eliminar la plantilla", variant: "destructive" });
      return;
    }
    if (selectedTemplate?.id === template.id) closeEditor();
    await loadAll();
  };

  const submitProcessLine = async (event) => {
    event.preventDefault();
    if (!selectedTemplate?.id || !processLine.process_id) return;
    const nextSortOrder = processLineEditingId ? processLine.sort_order : processLine.sort_order || processLines.length + 1;
    const result = await saveTemplateLine(
      {
        ...processLine,
        line_type: "process",
        template_id: selectedTemplate.id,
        sort_order: nextSortOrder,
        default_waste_pct: 0,
        fijo_al_duplicar: processLineEditingId
          ? Boolean(processLines.find((line) => line.id === processLineEditingId)?.fijo_al_duplicar)
          : false,
        mantener_precio_origen: processLineEditingId
          ? Boolean(processLines.find((line) => line.id === processLineEditingId)?.mantener_precio_origen)
          : false,
      },
      processLineEditingId
    );
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar el proceso", variant: "destructive" });
      return;
    }
    resetProcessLine();
    if (result.data?.id) setActiveProcessId(result.data.id);
    await loadAll(selectedTemplate.id);
  };

  const editProcessLine = (line) => {
    setActiveProcessId(line.id);
    setProcessLineEditingId(line.id);
    setProcessLine({
      line_type: "process",
      process_id: line.process_id || "",
      sort_order: line.sort_order || 0,
      default_quantity: line.default_quantity || 1,
      default_unit_cost: line.default_unit_cost ?? "",
      notes: line.notes || "",
    });
  };

  const openNewMaterialLine = () => {
    if (!activeProcess) return;
    setMaterialEditingLine(null);
    setMaterialDialogOpen(true);
  };

  const openEditMaterialLine = (line) => {
    setActiveProcessId(line.parent_template_line_id || activeProcess?.id || "");
    setMaterialEditingLine(toViewMaterialLine(line, materials));
    setMaterialDialogOpen(true);
  };

  const persistMaterialLine = async (payload) => {
    const parentProcessId = materialEditingLine?.parent_template_line_id || activeProcess?.id;
    if (!selectedTemplate?.id || !parentProcessId) {
      return { error: { message: "Selecciona un proceso y un material" } };
    }
    const result = await saveTemplateLine(
      {
        line_type: "material",
        template_id: selectedTemplate.id,
        parent_template_line_id: parentProcessId,
        material_id: payload.material_id,
        material_supplier_id: payload.material_supplier_id,
        default_quantity: payload.quantity,
        default_unit_cost: payload.unit_cost,
        default_waste_pct: 0,
        sort_order: payload.sort_order,
        notes: payload.notes,
        color_usage: payload.color_usage,
        requires_color: payload.requires_color,
        fijo_al_duplicar: false,
        solo_si_existe_destino: Boolean(payload.solo_si_existe_destino),
        excluir_al_duplicar: Boolean(payload.excluir_al_duplicar),
      },
      materialEditingLine?.id || null
    );
    if (!result.error) {
      await loadAll(selectedTemplate.id);
    }
    return result;
  };

  const removeLine = async (line) => {
    const message = line.line_type === "process"
      ? "¿Eliminar este proceso y sus materiales de la plantilla?"
      : "¿Eliminar este material de la plantilla?";
    if (!window.confirm(message)) return;

    const result = await deleteTemplateLine(line.id);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo eliminar la línea", variant: "destructive" });
      return;
    }
    if (line.id === activeProcessId) setActiveProcessId("");
    await loadAll(selectedTemplate?.id);
  };

  const reassignOrphanLine = async (line) => {
    if (!selectedTemplate?.id || !activeProcess) return;
    const result = await saveTemplateLine(
      {
        ...line,
        line_type: "material",
        template_id: selectedTemplate.id,
        parent_template_line_id: activeProcess.id,
      },
      line.id
    );
    if (result.error) {
      toast({ title: "Error", description: "No se pudo reubicar el material", variant: "destructive" });
      return;
    }
    await loadAll(selectedTemplate.id);
  };

  const reorderProcesses = async (reordered) => {
    if (!selectedTemplate) return;
    const nextLines = reordered.map((line, index) => ({ ...line, sort_order: index + 1 }));
    const orderById = new Map(nextLines.map((line) => [line.id, line.sort_order]));
    setSelectedTemplate((current) => {
      if (!current) return current;
      return {
        ...current,
        process_lines: nextLines,
        lines: (current.lines || []).map((line) =>
          orderById.has(line.id) ? { ...line, sort_order: orderById.get(line.id) } : line
        ),
      };
    });
    if (processLineEditingId) {
      const edited = nextLines.find((line) => line.id === processLineEditingId);
      if (edited) setProcessLine((current) => ({ ...current, sort_order: edited.sort_order }));
    }
    const result = await reorderTemplateLines(reordered);
    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo guardar el orden de los procesos",
        variant: "destructive",
      });
      await loadAll(selectedTemplate.id);
    }
  };

  const reorderMaterials = async (reordered) => {
    if (!selectedTemplate || !activeProcess) return;
    const nextLines = reordered.map((line, index) => ({ ...line, sort_order: index + 1 }));
    const orderById = new Map(nextLines.map((line) => [line.id, line.sort_order]));
    setSelectedTemplate((current) => {
      if (!current) return current;
      return {
        ...current,
        process_lines: (current.process_lines || []).map((process) =>
          process.id === activeProcess.id
            ? {
                ...process,
                child_lines: (process.child_lines || [])
                  .map((line) => (orderById.has(line.id) ? { ...line, sort_order: orderById.get(line.id) } : line))
                  .sort(sortByOrder),
              }
            : process
        ),
        lines: (current.lines || []).map((line) =>
          orderById.has(line.id) ? { ...line, sort_order: orderById.get(line.id) } : line
        ),
      };
    });
    if (materialEditingLine) {
      const edited = nextLines.find((line) => line.id === materialEditingLine.id);
      if (edited) setMaterialEditingLine(edited);
    }
    const result = await reorderTemplateLines(reordered);
    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo guardar el orden de los materiales",
        variant: "destructive",
      });
      await loadAll(selectedTemplate.id);
    }
  };

  const changeTemplateFamily = (codFam) => {
    const family = families.find((item) => item.cod_fam?.trim() === codFam);
    setFormData((current) => ({
      ...current,
      cod_fam: codFam,
      name: current.name.trim() ? current.name : family?.descrip || "",
    }));
  };

  const toggleMaterialExcluded = async (line, checked) => {
    const result = await setTemplateMaterialExcluded(line.id, checked);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar la exclusión", variant: "destructive" });
      return;
    }
    await loadAll(selectedTemplate.id);
  };

  const toggleMaterialDestinationOnly = async (line, checked) => {
    const result = await setTemplateMaterialDestinationOnly(line.id, checked);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar la regla del material", variant: "destructive" });
      return;
    }
    await loadAll(selectedTemplate.id);
  };

  const toggleFixedOnDuplicate = async (line, checked) => {
    if (!selectedTemplate?.id || !line?.id) return;
    const isProcess = line.line_type === "process" || Boolean(line.process_id && !line.material_id);
    if (!isProcess) return;
    const result = await saveTemplateLine(
      {
        template_id: selectedTemplate.id,
        line_type: isProcess ? "process" : "material",
        process_id: line.process_id,
        material_id: line.material_id,
        parent_template_line_id: line.parent_template_line_id,
        material_supplier_id: line.material_supplier_id,
        sort_order: line.sort_order,
        default_quantity: line.default_quantity ?? line.quantity,
        default_waste_pct: line.default_waste_pct ?? line.waste_pct ?? 0,
        default_unit_cost: line.default_unit_cost ?? line.unit_cost,
        notes: line.notes,
        color_usage: line.color_usage,
        requires_color: line.requires_color,
        fijo_al_duplicar: checked,
        mantener_precio_origen: Boolean(line.mantener_precio_origen),
      },
      line.id
    );
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar el valor fijo", variant: "destructive" });
      return;
    }
    await loadAll(selectedTemplate.id);
  };

  const toggleKeepOriginPrice = async (line, checked) => {
    if (!selectedTemplate?.id || !line?.id) return;
    const isProcess = line.line_type === "process" || Boolean(line.process_id && !line.material_id);
    if (!isProcess) return;
    const result = await saveTemplateLine(
      {
        template_id: selectedTemplate.id,
        line_type: isProcess ? "process" : "material",
        process_id: line.process_id,
        material_id: line.material_id,
        parent_template_line_id: line.parent_template_line_id,
        material_supplier_id: line.material_supplier_id,
        sort_order: line.sort_order,
        default_quantity: line.default_quantity ?? line.quantity,
        default_waste_pct: line.default_waste_pct ?? line.waste_pct ?? 0,
        default_unit_cost: line.default_unit_cost ?? line.unit_cost,
        notes: line.notes,
        color_usage: line.color_usage,
        requires_color: line.requires_color,
        fijo_al_duplicar: Boolean(line.fijo_al_duplicar),
        mantener_precio_origen: checked,
      },
      line.id
    );
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar el proceso tejido", variant: "destructive" });
      return;
    }
    await loadAll(selectedTemplate.id);
  };

  const printTemplates = async (templateId = null) => {
    setPrinting(true);
    try {
      const result = await fetchTemplates();
      if (result.error) throw result.error;
      const selected = templateId ? result.data.filter((template) => template.id === templateId) : result.data;
      if (!selected.length) throw new Error("No hay plantillas guardadas para imprimir.");
      const { buildTemplatesPdf } = await import("@/lib/manufacturingDocuments");
      const pdf = await buildTemplatesPdf(selected);
      setPrintUrl(URL.createObjectURL(new Blob([await pdf.save()], {type:"application/pdf"})));
    } catch (error) {
      toast({ title: "No se pudo imprimir", description: error.message, variant: "destructive" });
    } finally {
      setPrinting(false);
    }
  };
  const printPreview = <PdfPreview url={printUrl} onClose={() => setPrintUrl(null)} title="Plantillas completas - versión guardada" />;

  const takenFamilyCodes = new Set(
    templates
      .filter((template) => template.id !== editingId && template.cod_fam)
      .map((template) => template.cod_fam.trim())
  );

  if (!editorOpen) {
    return (
      <div className="space-y-4">
        {printPreview}
        <div className="flex flex-col justify-between gap-3 rounded-lg border bg-white p-4 shadow-sm md:flex-row md:items-center">
          <div>
            <h2 className="text-xl font-semibold">Plantillas</h2>
            <div className="text-sm text-gray-600">{templates.length} plantillas creadas</div>
          </div>
          <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={printing || !templates.length} onClick={() => printTemplates()}>
            <Printer className="mr-2 h-4 w-4" />{printing ? "Preparando PDF..." : "Imprimir todas"}
          </Button>
          <Button type="button" onClick={openNewTemplate}>
            <Plus className="mr-2 h-4 w-4" />
            Nueva plantilla
          </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {templates.map((template) => {
            const cardTotals = templateTotals(template);
            return (
              <div key={template.id} className="rounded-lg border bg-white p-4 shadow-sm">
                <button type="button" className="block w-full text-left" onClick={() => openTemplate(template)}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold">{template.name}</h3>
                      <p className="mt-1 text-sm font-medium text-gray-800">
                        {template.cod_fam || template.family ? formatFamilyTemplateOption(template) : "Sin familia"}
                      </p>
                      <p className="mt-1 line-clamp-2 text-sm text-gray-600">{template.description || "Sin descripción"}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs ${template.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                      {template.is_active ? "Activa" : "Inactiva"}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                    <div className="rounded bg-gray-50 px-2 py-1">
                      <span className="block text-xs text-gray-500">Procesos</span>
                      <b>{template.process_lines?.length || 0}</b>
                    </div>
                    <div className="rounded bg-gray-50 px-2 py-1">
                      <span className="block text-xs text-gray-500">Materiales</span>
                      <b>{(template.lines || []).filter((line) => line.line_type === "material").length}</b>
                    </div>
                    <div className="rounded bg-gray-50 px-2 py-1">
                      <span className="block text-xs text-gray-500">Coste</span>
                      <b>{formatMoney(cardTotals.total)}</b>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-500">{templateKindLabels[template.template_kind] || template.template_kind}</div>
                </button>
                <div className="mt-3 flex justify-end gap-2">
                  <Button type="button" variant="outline" size="sm" title="Imprimir plantilla completa" disabled={printing} onClick={() => printTemplates(template.id)}>
                    <Printer className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" title="Editar" onClick={() => openTemplate(template)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="destructive" size="sm" title="Eliminar" onClick={() => removeTemplate(template)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
          {templates.length === 0 && (
            <div className="rounded-lg border border-dashed bg-white p-6 text-center text-sm text-gray-500 md:col-span-2 2xl:col-span-3">
              No hay plantillas.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {printPreview}
      <form onSubmit={submitTemplate} className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={closeEditor}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
            <h2 className="text-xl font-semibold">{editingId ? "Editar plantilla" : "Nueva plantilla"}</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" disabled={printing || !selectedTemplate?.id} onClick={() => printTemplates(selectedTemplate.id)} title="Imprimir la versión guardada con todos sus procesos y materiales">
              <Printer className="mr-2 h-4 w-4" />{printing ? "Preparando PDF..." : "Imprimir plantilla"}
            </Button>
            <Button type="submit"><Save className="mr-2 h-4 w-4" />Guardar plantilla</Button>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-12">
          <select
            required
            className="rounded-md border border-input bg-background px-3 py-2 lg:col-span-3"
            value={formData.cod_fam}
            onChange={(event) => changeTemplateFamily(event.target.value)}
          >
            <option value="">Familia</option>
            {families.map((family) => {
              const code = family.cod_fam.trim();
              const taken = takenFamilyCodes.has(code);
              return (
                <option key={family.cod_fam} value={code} disabled={taken}>
                  {code} · {family.descrip}
                  {taken ? " (ya tiene plantilla)" : ""}
                </option>
              );
            })}
          </select>
          <Input
            required
            className="lg:col-span-3"
            placeholder="Nombre"
            value={formData.name}
            onChange={(event) => setFormData({ ...formData, name: event.target.value })}
          />
          <Input
            className="lg:col-span-2"
            placeholder="Descripción"
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
          />
          <select
            className="rounded-md border border-input bg-background px-3 py-2 lg:col-span-2"
            value={formData.template_kind}
            onChange={(event) => setFormData({ ...formData, template_kind: event.target.value })}
          >
            <option value="mixed">Proceso con materiales</option>
            <option value="processes">Solo procesos</option>
            <option value="materials">Legacy plano</option>
          </select>
          <label className="flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm lg:col-span-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(event) => setFormData({ ...formData, is_active: event.target.checked })}
            />
            Activa
          </label>
        </div>
      </form>

      <div className="rounded-lg border bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b p-4">
          <div>
            <h3 className="font-semibold">{selectedTemplate?.name || "Plantilla sin guardar"}</h3>
            <div className="text-xs text-gray-600">
              Procesos {formatMoney(totals.processTotal)} · Materiales {formatMoney(totals.materialTotal)} ·{" "}
              <span className="font-bold text-gray-900">Total {formatMoney(totals.total)}</span>
            </div>
            <div className="mt-2 space-y-1 text-xs text-gray-600">
              <p className="font-medium">Al duplicar a otra familia: Fijo y Tejido se consultan en la plantilla origen; las marcas de materiales, en la plantilla destino:</p>
              <ul className="list-disc space-y-1 pl-4">
                <li>{DUPLICATE_FAMILY_RULES.membership}</li>
                    <li>{DUPLICATE_FAMILY_RULES.fixed}</li>
                <li>{DUPLICATE_FAMILY_RULES.destination}</li>
                <li>{DUPLICATE_FAMILY_RULES.fabric}</li>
                <li>{DUPLICATE_FAMILY_RULES.exclude}</li>
                <li>{DUPLICATE_FAMILY_RULES.material} La duplicación consulta las marcas actuales de las plantillas; se aplican también a artículos ya creados.</li>
              </ul>
              <p>Las marcas se aplican al proceso y a sus materiales. Los materiales sin proceso se toman del destino salvo los excluidos; Solo si existe en el artículo origen requiere existir sin proceso en ese artículo. Si no hay plantilla destino, se copia el escandallo de origen. Las tarifas se recalculan con el escandallo resultante.</p>
            </div>
          </div>
        </div>

        {selectedTemplate ? (
          <div className="p-4">
            <CostSheetProcessMaterialsView
              processLines={viewProcessLines}
              activeProcessId={activeProcess?.id}
              onActiveProcessChange={setActiveProcessId}
              onReorderProcesses={reorderProcesses}
              onReorderMaterials={reorderMaterials}
              sheetTotals={sheetTotals}
              emptyProcessMessage="Sin procesos."
              emptyMaterialMessage="Sin materiales."
              materialHeaderAction={(
                <Button type="button" size="sm" onClick={openNewMaterialLine} disabled={!activeProcess}>
                  <Plus className="mr-2 h-4 w-4" />
                  Añadir material
                </Button>
              )}
              processFooter={(
                <form onSubmit={submitProcessLine} className="grid gap-2 border-t p-3 sm:grid-cols-12">
                  <select
                    className="rounded-md border border-input bg-background px-2 py-2 text-sm sm:col-span-7"
                    value={processLine.process_id}
                    onChange={(event) => setProcessLine({ ...processLine, process_id: event.target.value })}
                  >
                    <option value="">Proceso</option>
                    {processes.map((process) => (
                      <option key={process.id} value={process.id}>
                        {process.description}
                      </option>
                    ))}
                  </select>
                  <Input
                    className="sm:col-span-3"
                    type="number"
                    min="0"
                    step="0.0001"
                    value={processLine.default_unit_cost}
                    onChange={(event) => setProcessLine({ ...processLine, default_unit_cost: event.target.value })}
                    placeholder="Precio"
                  />
                  <Button className="sm:col-span-2" type="submit" size="sm" title={processLineEditingId ? "Actualizar" : "Añadir"}>
                    {processLineEditingId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </Button>
                  <Input
                    className="sm:col-span-4"
                    type="number"
                    placeholder="Orden"
                    value={processLine.sort_order}
                    onChange={(event) => setProcessLine({ ...processLine, sort_order: event.target.value })}
                  />
                  <Input
                    className="sm:col-span-8"
                    value={processLine.notes}
                    onChange={(event) => setProcessLine({ ...processLine, notes: event.target.value })}
                    placeholder="Observaciones"
                  />
                  {processLineEditingId && (
                    <Button className="sm:col-span-12" type="button" variant="ghost" size="sm" onClick={resetProcessLine}>
                      <X className="mr-1 h-4 w-4" />
                      Cancelar edición
                    </Button>
                  )}
                </form>
              )}
              renderProcessActions={(line) => (
                <>
                  <label
                    className="flex items-center gap-1 text-xs text-gray-600"
                    title={DUPLICATE_FAMILY_RULES.fixed}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(line.fijo_al_duplicar)}
                      onChange={(event) => toggleFixedOnDuplicate(line, event.target.checked)}
                    />
                    Fijo
                  </label>
                  <label
                    className="flex items-center gap-1 text-xs text-gray-600"
                    title={DUPLICATE_FAMILY_RULES.fabric}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(line.mantener_precio_origen)}
                      onChange={(event) => toggleKeepOriginPrice(line, event.target.checked)}
                    />
                    Tejido
                  </label>
                  <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" title="Editar proceso" onClick={() => editProcessLine(line)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="destructive" size="sm" className="h-8 w-8 p-0" title="Eliminar proceso" onClick={() => removeLine(line)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
              renderMaterialActions={(line) => (
                <>
                  <label className="flex items-center gap-1 text-xs text-gray-600"
                    title="Al duplicar hacia esta familia, solo se incorpora si existe en el mismo proceso del artículo origen. Excluir al duplicar tiene prioridad.">
                    <input type="checkbox" checked={Boolean(line.solo_si_existe_destino)}
                      onChange={(event) => toggleMaterialDestinationOnly(line, event.target.checked)} />
                    Solo si existe en el artículo origen
                  </label>
                  <label className="flex items-center gap-1 text-xs text-gray-600"
                    title="Al duplicar hacia esta familia se omite este material, aunque exista en origen. Tiene prioridad sobre Fijo, Tejido y la otra casilla.">
                    <input type="checkbox" checked={Boolean(line.excluir_al_duplicar)}
                      onChange={(event) => toggleMaterialExcluded(line, event.target.checked)} />
                    Excluir al duplicar
                  </label>
                  <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" title="Editar material" onClick={() => openEditMaterialLine(line)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="destructive" size="sm" className="h-8 w-8 p-0" title="Eliminar material" onClick={() => removeLine(line)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            />
          </div>
        ) : (
          <div className="p-4 text-sm text-gray-500">Guarda la plantilla para añadir procesos y materiales.</div>
        )}
      </div>

      {selectedTemplate && orphanLines.length > 0 && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900 shadow-sm">
          <h3 className="mb-2 font-semibold">Materiales sin proceso en plantilla antigua</h3>
          <div className="space-y-2">
            {orphanLines.map((line) => (
              <div key={line.id} className="flex flex-col justify-between gap-2 rounded border bg-white p-2 md:flex-row md:items-center">
                <span>{materialLabel(line, materials)} · {formatMoney(materialCost(line))}</span>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" disabled={!activeProcess} onClick={() => reassignOrphanLine(line)}>
                    Asignar a {activeProcess ? processLabel(activeProcess, processes) : "proceso"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => openEditMaterialLine(line)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="destructive" size="sm" onClick={() => removeLine(line)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CostSheetMaterialLineDialog
        showDuplicateRule
        duplicateRuleDescription="Al duplicar hacia esta familia, solo se incorpora este material si ya existe en el mismo proceso del artículo origen. Se aplica con y sin Fijo. Tejido conserva el origen salvo Excluir al duplicar. No afecta al alta de artículos."
        open={materialDialogOpen}
        onOpenChange={(open) => {
          setMaterialDialogOpen(open);
          if (!open) setMaterialEditingLine(null);
        }}
        processDescription={activeProcess ? processLabel(activeProcess, processes) : ""}
        hasProcess={Boolean(activeProcess)}
        editingLine={materialEditingLine}
        currentMaterialCount={activeProcess?.child_lines?.length || 0}
        materials={materials}
        suppliers={suppliers}
        onMaterialsChange={setMaterials}
        onSave={persistMaterialLine}
      />
    </div>
  );
}
